import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  selectCanvas,
  selectCurrentPageOriginalHeight,
  selectCurrentPageOriginalRotationAngle,
  selectCurrentPageOriginalWidth,
  selectFile,
  selectPage,
  selectScale,
  selectTotalPages,
  selectUrl,
} from '../../reducers/pdf-viewer/selectors';
import {
  setCanvasCurrentPageOriginalDimensions,
  setCanvasDimensions,
  setCanvasMarginLeft,
  setPdfPage,
  setPdfScale,
  setPdfTotalPages,
} from '../../reducers/pdf-viewer/actions';
import { isNil, propOr } from 'ramda';
import ReactResizeDetector from 'react-resize-detector';
import { Document, Page, pdfjs } from 'react-pdf';
import ReactCursorPosition from 'react-cursor-position';
import classes from './base-pdf-viewer.module.scss';
import PdfToolbar from './atomics/pdf-toolbar';
import { setCurrentPointerMode } from '../../reducers/editor/actions';
import { Spin } from 'antd';

// Configure PDF.js worker (required since v6+). Works with CRA/webpack 5.
pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`;

const basePDFViewer = (WrapperComponent) => {
  const BasePDFViewer = (props) => {
    const pageRef = useRef(null);
    const reactCursorPositionRef = useRef(null);

    const documentOptions = useMemo(() => ({ isEvalSupported: false }), []);

    const pdfFile = useSelector(selectFile);
    const pdfUrl = useSelector(selectUrl);
    const currentEditorPage = useSelector(selectPage);
    const currentEditorScale = useSelector(selectScale);
    const canvas = useSelector(selectCanvas);
    const currentPageOriginalHeight = useSelector(
      selectCurrentPageOriginalHeight
    );
    const currentPageOriginalWidth = useSelector(
      selectCurrentPageOriginalWidth
    );
    const currentPageOriginalRotationAngle = useSelector(
      selectCurrentPageOriginalRotationAngle
    );
    const totalPages = useSelector(selectTotalPages);

    const dispatch = useDispatch();

    const height = window.innerHeight;

    const [page, setPage] = useState(1);
    const [scale, setScale] = useState(100);
    const [position, setPosition] = useState(null);

    const convertYToPdfUnits = (pixelPosition) => {
      let baseCanvasLength = -1;
      // if we have 270 rotation then we change to use canvas width instead
      if (
        currentPageOriginalRotationAngle === 270 ||
        currentPageOriginalRotationAngle === 90
      ) {
        baseCanvasLength = canvas.canvasWidth;
      } else {
        baseCanvasLength = canvas.canvasHeight;
      }
      return Math.round(
        (pixelPosition * currentPageOriginalHeight) / baseCanvasLength
      );
    };

    const convertXToPdfUnits = (pixelPosition) => {
      let baseCanvasLength = -1;
      // if we have 270 rotation then we change to use canvas height instead
      if (
        currentPageOriginalRotationAngle === 270 ||
        currentPageOriginalRotationAngle === 90
      ) {
        baseCanvasLength = canvas.canvasHeight;
      } else {
        baseCanvasLength = canvas.canvasWidth;
      }
      return Math.round(
        (pixelPosition * currentPageOriginalWidth) / baseCanvasLength
      );
    };

    const setCurrentEditorPage = (page) => {
      if (page > 0 && page <= totalPages) {
        return dispatch(setPdfPage(+page));
      }
      return dispatch(setPdfPage(1));
    };

    const changePage = (page) => {
      setPage(page);
      setCurrentEditorPage(page);
    };

    const handleDocumentLoadSuccess = (document) => {
      dispatch(setPdfPage(1));
      dispatch(setPdfTotalPages(document.numPages));
    };

    const handlePageLoadSuccess = (page) => {
      dispatch(
        setCanvasCurrentPageOriginalDimensions(
          page.originalHeight,
          page.originalWidth,
          page._pageInfo.rotate
        )
      );
      // saveCanvasSizes();
    };

    const setCurrentEditorScale = (scale) => {
      if (scale > 0) {
        return dispatch(setPdfScale(scale / 100));
      }
      return dispatch(setPdfScale(1));
    };

    const resetPage = () => {
      if (currentEditorPage !== +page) {
        changePage(1);
      }
    };

    const resetZoom = () => {
      if (currentEditorScale !== scale / 100) {
        changeZoom(100);
      }
    };

    const changeZoom = (scale) => {
      setScale(scale);
    };

    // const cursorPositionInPixels = (e) => {
    //   const bounds = e.target.getBoundingClientRect();
    //   const x = e.clientX - bounds.left;
    //   const y = e.clientY - bounds.top;
    //   return { x: x, y: y };
    // };

    const saveCanvasSizes = () => {
      if (
        pageRef &&
        reactCursorPositionRef &&
        !isNil(pageRef.current) &&
        !isNil(reactCursorPositionRef.current)
      ) {
        const canvas = pageRef.current.querySelector("canvas");

        if (canvas) {
          dispatch(
            setCanvasMarginLeft(
              pageRef.current.offsetWidth > canvas.offsetWidth
                ? (pageRef.current.offsetWidth - canvas.offsetWidth) / 2
                : 0
            )
          );
          dispatch(
            setCanvasDimensions(canvas.offsetHeight, canvas.offsetWidth)
          );
        }

        reactCursorPositionRef.current.reset();
      }
    };

    const cursorLeftPdf = useMemo(() => {
      if (!position || !pageRef.current) return 0;

      let pixelPositionLeft = Math.abs(
        position.x -
          canvas.canvasMarginLeft +
          pageRef.current.parentElement.scrollLeft
      );
      return convertXToPdfUnits(pixelPositionLeft);
    }, [position, canvas.canvasMarginLeft, pageRef]);

    const cursorTopPdf = useMemo(() => {
      if (!position || !pageRef.current) return 0;

      let pixelPositionTop = Math.abs(
        position.y + pageRef.current.parentElement.scrollTop
      );
      return convertYToPdfUnits(pixelPositionTop);
    }, [position, pageRef]);

    const onPositionChanged = (data) => {
      setPosition(data.position);
    };

    const pageWrapperWidth = useMemo(() => {
      return pageRef.current
        ? pageRef.current.offsetWidth + pageRef.current.parentElement.scrollLeft
        : 0;
    }, [pageRef.current, pageRef]);

    const pageWrapperHeight = useMemo(() => {
      return pageRef.current ? pageRef.current.offsetHeight : 0;
    }, [
      pageRef.current,
      pageRef,
      propOr(null, "offsetHeight", pageRef.current),
    ]);

    useEffect(() => {
      setCurrentEditorScale(scale);
    }, [scale]);

    useEffect(() => {
      dispatch(setCurrentPointerMode(propOr(null, 0, props.acceptedModes)));
    }, [props.acceptedModes]);

    return (
      <div style={{ height: "100%", display: "flex", flexDirection: "column" }}>
        <div className={classes.PdfToolbar}>
          <PdfToolbar
            acceptedModes={props.acceptedModes}
            changePage={changePage}
            totalPages={totalPages}
            resetPage={resetPage}
            scale={scale}
            setZoom={changeZoom}
            resetZoom={resetZoom}
            cursorTopPdf={cursorTopPdf}
            cursorLeftPdf={cursorLeftPdf}
          />
        </div>

        <div style={{ flex: "1 0 1px", overflow: "auto" }}>
          {(pdfUrl || pdfFile) && (
            <Document
              className={classes.Document}
              file={pdfUrl || pdfFile}
              noData=""
              onLoadSuccess={handleDocumentLoadSuccess}
              onLoadError={console.error}
              options={documentOptions}
              loading={
                <div className={classes.LoadingPDF}>
                  <Spin size={"large"}/> <span className={"text-primary "}>Loading PDF ...</span>
                </div>
              }
            >
              <ReactCursorPosition
                ref={(rcp) => {
                  if (rcp) {
                    reactCursorPositionRef.current = rcp;
                  } else {
                    reactCursorPositionRef.current = null;
                  }
                }}
                onPositionChanged={onPositionChanged}
              >
                <WrapperComponent
                  {...props}
                  width={pageWrapperWidth}
                  height={pageWrapperHeight}
                  cursorTopPdf={cursorTopPdf}
                  cursorLeftPdf={cursorLeftPdf}
                  pageRef={pageRef}
                  convertXToPdfUnits={convertXToPdfUnits}
                  convertYToPdfUnits={convertYToPdfUnits}
                />
                <Page
                  pageNumber={currentEditorPage}
                  scale={currentEditorScale}
                  renderTextLayer={false}
                  renderAnnotationLayer={false}
                  height={height}
                  onLoadSuccess={handlePageLoadSuccess}
                  inputRef={(ref) => (pageRef.current = ref)}
                  onRenderSuccess={saveCanvasSizes}
                />

                <ReactResizeDetector
                  handleWidth
                  handleHeight
                  onResize={saveCanvasSizes}
                />
              </ReactCursorPosition>
            </Document>
          )}
        </div>
      </div>
    );
  };

  return BasePDFViewer;
};

export default basePDFViewer;
